import React, { useEffect, useRef, useState } from 'react';
import { Input } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FiChevronRight, FiSearch, FiX, FiMapPin } from 'react-icons/fi';
import Modal from '../../ui/modal/Modal';
import { formatPrice } from '../../../utils/priceFormatter';
import { api } from '../../../services/api';
import { openCityModal } from '../../../store/slices/citySlice';
import { unpackProductSearchResults, getProductSearchPath } from './searchUtils';
import SearchShimmer from './SearchShimmer';

const MIN_SEARCH_LENGTH = 2;
const SEARCH_LIMIT = 5;
const SEARCH_DELAY_MS = 350;

const ProductResult = ({ item, isDarkMode, compact, onSelect, city }) => {
  const path = getProductSearchPath(item, city);
  const price = Number(item.price);
  const hasPrice = item.price !== null && item.price !== undefined &&
    item.price !== '' && Number.isFinite(price) && price > 0;

  return (
    <button
      type="button"
      role="option"
      aria-selected={false}
      disabled={!path}
      onClick={() => onSelect(item, path)}
      className={`flex items-center w-full gap-3 text-left rounded-md transition-colors ${
        compact ? 'px-3 py-2' : 'p-3'
      } ${isDarkMode ? 'text-gray-100 hover:bg-gray-700 focus-visible:bg-gray-700'
          : 'text-gray-800 hover:bg-gray-50 focus-visible:bg-gray-50'}
        ${!path ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
    >
      <div className={`shrink-0 rounded-md border p-1 ${compact ? 'w-10 h-10' : 'w-12 h-12'} ${
        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
      }`}>
        {item.image ? (
          <img src={item.image} alt="" loading="lazy" className="object-contain w-full h-full"
            onError={(event) => { event.currentTarget.style.visibility = 'hidden'; }} />
        ) : (
          <FiSearch aria-hidden="true" className="w-full h-full p-2 text-gray-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{item.title || item.name || 'Product'}</div>
        <div className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-xs ${
          isDarkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>
          {hasPrice && <span className="font-semibold text-green-600 dark:text-green-400">Up to {formatPrice(price)}</span>}
          {item.category?.display_name || item.category?.name ? (
            <span className="truncate">{item.category.display_name || item.category.name}</span>
          ) : null}
        </div>
      </div>
      {path && <FiChevronRight aria-hidden="true" className="shrink-0 text-gray-400" />}
    </button>
  );
};

const SearchModal = ({
  visible = false,
  onClose,
  isDarkMode,
  isDesktop = false,
  inline = false,
  placeholderTexts = ['Search for Mobile Phones...', 'Search for Tablets...', 'Search for Smart Watches...'],
  onResultClick,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedCity = useSelector((state) => state.city.selectedCity);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [retryIndex, setRetryIndex] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const requestNumber = useRef(0);
  const trimmedQuery = query.trim();
  const canSearch = trimmedQuery.length >= MIN_SEARCH_LENGTH && trimmedQuery.length <= 100;
  const inlineMode = isDesktop || inline;
  const isActive = inlineMode || visible;

  useEffect(() => {
    if (!placeholderTexts.length) return undefined;
    const timer = setInterval(() => {
      setPlaceholderIndex((index) => (index + 1) % placeholderTexts.length);
    }, 2500);
    return () => clearInterval(timer);
  }, [placeholderTexts.length]);

  useEffect(() => {
    if (!inlineMode && visible) inputRef.current?.focus();
  }, [inlineMode, visible]);

  useEffect(() => {
    const id = ++requestNumber.current;
    if (!isActive || !canSearch || !selectedCity?.id) {
      setResults([]);
      setLoading(false);
      setSearched(false);
      setError('');
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setResults([]);
    setError('');
    setSearched(false);

    const timer = setTimeout(async () => {
      try {
        const response = await api.searchProducts(trimmedQuery, {
          cityId: selectedCity.id,
          limit: SEARCH_LIMIT,
          offset: 0,
          signal: controller.signal,
        });
        if (id !== requestNumber.current || controller.signal.aborted) return;
        setResults(unpackProductSearchResults(response).slice(0, SEARCH_LIMIT));
        setSearched(true);
      } catch (err) {
        if (id !== requestNumber.current || controller.signal.aborted) return;
        setResults([]);
        setError(err.response?.data?.message || err.message || 'Search failed. Please try again.');
        setSearched(true);
      } finally {
        if (id === requestNumber.current && !controller.signal.aborted) setLoading(false);
      }
    }, SEARCH_DELAY_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [trimmedQuery, canSearch, selectedCity?.id, isActive, retryIndex]);

  useEffect(() => {
    if (!inlineMode) return undefined;
    const onOutsideClick = (event) => {
      if (!searchRef.current?.contains(event.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', onOutsideClick);
    return () => document.removeEventListener('mousedown', onOutsideClick);
  }, [inlineMode]);

  const clearSearch = () => {
    ++requestNumber.current;
    setQuery('');
    setResults([]);
    setLoading(false);
    setSearched(false);
    setError('');
    inputRef.current?.focus();
  };

  const handleChange = (event) => {
    ++requestNumber.current; // invalidate a previous response immediately
    setQuery(event.target.value);
    setResults([]);
    setSearched(false);
    setError('');
    setDropdownOpen(true);
  };

  const closeSearch = () => {
    setDropdownOpen(false);
    if (!inlineMode) clearSearch();
    onClose?.();
  };

  const selectResult = (item, path) => {
    if (!path) return;
    if (onResultClick) onResultClick(item);
    else navigate(path);
    closeSearch();
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (inlineMode) setDropdownOpen(false);
      else closeSearch();
    } else if (event.key === 'Enter' && results.length && canSearch) {
      event.preventDefault();
      selectResult(results[0], getProductSearchPath(results[0], selectedCity));
    }
  };

  const searchInput = (
    <Input
      ref={inputRef}
      aria-label="Search products"
      aria-expanded={inlineMode ? dropdownOpen && !!trimmedQuery : undefined}
      placeholder={placeholderTexts[placeholderIndex] || 'Search products...'}
      prefix={<FiSearch aria-hidden="true" className="text-gray-400" />}
      suffix={query && (
        <button type="button" aria-label="Clear search" onClick={clearSearch}
          className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600">
          <FiX aria-hidden="true" className="text-gray-500 dark:text-gray-300" />
        </button>
      )}
      value={query}
      onChange={handleChange}
      onKeyDown={onKeyDown}
      onFocus={() => setDropdownOpen(true)}
      maxLength={100}
      className={`w-full h-10 text-sm rounded-lg border ${
        isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'
      }`}
    />
  );

  const content = !selectedCity?.id ? (
    <div className="px-4 py-5 text-center text-sm">
      <p className="mb-3 text-gray-600 dark:text-gray-300">Choose your city to search available products.</p>
      <button type="button" onClick={() => dispatch(openCityModal())}
        className="inline-flex items-center gap-2 px-3 py-2 font-medium text-white bg-green-600 rounded-md hover:bg-green-700">
        <FiMapPin aria-hidden="true" /> Select city
      </button>
    </div>
  ) : !canSearch ? (
    <p className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">
      Type at least 2 characters to search products.
    </p>
  ) : loading ? (
    <SearchShimmer isDesktop={inlineMode} />
  ) : error ? (
    <div role="alert" className="px-4 py-6 text-sm text-center text-red-600 dark:text-red-400">
      <p>{error}</p>
      <button type="button" className="mt-2 font-medium underline"
        onClick={() => setRetryIndex((current) => current + 1)}>
        Try again
      </button>
    </div>
  ) : searched && !results.length ? (
    <div className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">
      No products found. Try different keywords.
    </div>
  ) : results.length ? (
    <div role="listbox" aria-label="Matching products" className="max-h-80 overflow-y-auto">
      {results.map((item) => (
        <ProductResult key={item.id ?? item.slug} item={item} city={selectedCity}
          compact={inlineMode} isDarkMode={isDarkMode} onSelect={selectResult} />
      ))}
    </div>
  ) : null;

  if (inlineMode) {
    return (
      <div ref={searchRef} className="relative w-full">
        {searchInput}
        {dropdownOpen && (trimmedQuery || !selectedCity?.id) && (
          <div className={`absolute top-full left-0 right-0 z-50 mt-1 overflow-hidden rounded-lg border shadow-xl ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {content}
          </div>
        )}
      </div>
    );
  }

  return (
    <Modal visible={visible} onClose={closeSearch} title={
      <div className="flex items-center gap-2 text-gray-900 dark:text-white">
        <FiSearch aria-hidden="true" className="text-green-500" /> Search
      </div>
    } size="full" position="top" animation="slide-down" className="mt-0">
      <div className="p-3">
        <div className="mb-3">{searchInput}</div>
        <div className="overflow-y-auto max-h-[calc(100vh-180px)]">{content}</div>
      </div>
    </Modal>
  );
};

export default SearchModal;
